/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import { makeStyles } from '@material-ui/core/styles';
import { shape, string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import LinkOrATag from '../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import { getSideBarViewCart } from '../../../../state/ducks/App/App-Selectors';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    container: (data) => ({
        backgroundColor: data?.styling?.background_color?.color || '#146E37',
        fontSize: data?.styling?.font_size || '15px',
        fontFamily: data?.styling?.font_family,
        height: data?.styling?.height || '35px',
        width: data?.styling?.width || '100%',
        display: 'flex',
        flexWrap: 'wrap',
        boxSizing: 'border-box',
        padding: '4px 16px',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
            padding: '4px 7px',
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: 11,
            padding: 6,
        },
        [theme.breakpoints.down(380)]: {
            fontSize: 10,
        },
        '& a': {
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            textDecoration: 'none',
            color: data?.styling?.font_color?.color || '#ffffff',
        },
    }),
    para: (data) => ({
        lineHeight: '1.2',
        letterSpacing: '.04em',
        color: data?.styling?.font_color?.color || '#ffffff',
        fontWeight: '400',
        display: 'flex',
        textAlign: 'left',
        alignItems: 'flex-end',
        [theme.breakpoints.down('xs')]: {
            letterSpacing: '-0.2px',
            textAlign: 'center',
            alignItems: 'center',
        },
        '& p': {
            margin: 0,
            padding: '4px 5px',
            '& a': {
                color: data?.styling?.font_color?.color || '#ffffff',
            },
        },
        '& img': {
            [theme.breakpoints.down('500')]: {
                paddingTop: 3,
            },
        },
    }),
    eyeBrowImage: {
        width: 14,
    },
    '@media screen and (min-width: 1120px) and (max-width: 1600px)': {
        topBannerStyle: {
            fontSize: '13px',
        },
    },
}));

const AboveBannerEyebrow = ({ location, data }) => {
    const dispatch = useDispatch();
    const isSideMiniCartOpen = useSelector(getSideBarViewCart);
    const pageType = useSelector(getCurrentPageType);

    useEffect(() => {
        if (data?.tracking_for_impression) {
            dispatch(trackEvent({
                eventCategory: data?.tracking_for_impression?.tracking_event_category || '',
                eventAction: data?.tracking_for_impression?.tracking_event_action || '',
                eventLabel: data?.tracking_for_impression?.tracking_event_label || '',
                nonInteraction: true,
            }));
        }
    }, [location.pathname]);

    const handleClickEvent = () => {
        if (data?.tracking_for_click) {
            dispatch(trackEvent({
                eventCategory: data?.tracking_for_click?.tracking_event_category || '',
                eventAction: data?.tracking_for_click?.tracking_event_action || '',
                eventLabel: `${data?.tracking_for_click?.tracking_event_label || ''} ${pageType}`,
                eventValue: '1',
            }));
        }
    };

    const classes = useStyles(data);

    // return Banner Grid
    const bannerGrid = () => (
        <div
            className={classes.para}
            role="button"
            tabIndex={0}
            onClick={handleClickEvent}
            onKeyDown={handleClickEvent}
        >
            {data.message?.image_left?.url &&  <ResponsiveImage path={data.message.image_left.url} alt={data.message?.copy} className={classes.eyeBrowImage} /> }
            <ReactMarkdown source={data?.message?.copy} escapeHtml={false} />
            {data.message?.image_right?.url &&  <ResponsiveImage path={data.message.image_right.url} alt={data.message?.copy} className={classes.eyeBrowImage} /> }
        </div>
    );

    // return Banner based on anchor link
    const bannerBlock = () => (data?.link_url ? (
        <LinkOrATag href={data.link_url} className={isSideMiniCartOpen ? classes.topBannerStyle : ''}>
            {bannerGrid()}
        </LinkOrATag>
    ) : bannerGrid());

    return  data ? (
        <Grid container className={classes.container}>
            {bannerBlock()}
        </Grid>
    ) : null;
};

AboveBannerEyebrow.propTypes = {
    location: shape({
        location: string,
    }).isRequired,
    data: shape({
        link_url: string,
        message: shape({
            copy: string,
            image_left: shape({
                url: string,
            }),
            image_right: shape({
                url: string,
            }),
        }),
        styling: shape({
            background_color: shape({
                color: string,
            }),
            font_color: shape({
                color: string,
            }),
            font_family: string,
            font_size: string,
            width: string,
            height: string,
        }),
        tracking_for_impression: shape({
            tracking_event_category: string,
            tracking_event_action: string,
            tracking_event_label: string,
        }),
        tracking_for_click: shape({
            tracking_event_category: string,
            tracking_event_action: string,
            tracking_event_label: string,
        }),
    }).isRequired,
};

export default AboveBannerEyebrow;
